import type React from 'react';

import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type EmptyFunction } from '@/utils/emptyFunction.util.ts';

type Props = {
  closeModal: () => void;
  customLabel?: string;
  disableDeleteButton?: boolean;
  disableSubmit?: boolean;
  editMode?: boolean;
  handleDelete?: () => void;
  isPending?: boolean;
  onSubmit?: EmptyFunction;
  submitLabel?: string;
};

export const ModalButtons: React.FC<Props> = ({
  closeModal,
  customLabel,
  disableDeleteButton,
  disableSubmit,
  editMode,
  handleDelete,
  isPending,
  onSubmit,
  submitLabel,
}) => {
  const { t } = useTranslation();
  const submitCustomLabel = useMemo(() => {
    if (!submitLabel) {
      return t('profile.forms.buttonSave');
    }
    return {
      confirm: t('profile.forms.buttonConfirm'),
      default: t('profile.forms.buttonSave'),
      send: t('profile.forms.buttonSend'),
    }[submitLabel];
  }, [submitLabel, t]);

  return (
    <Stack direction="row" spacing={1}>
      {editMode && !disableDeleteButton && (
        <Button color="warning" disabled={isPending} onClick={handleDelete}>
          {t('profile.forms.buttonDelete')}
        </Button>
      )}
      <Button disabled={isPending} onClick={closeModal} variant="outlined">
        {t('profile.forms.buttonCancel')}
      </Button>
      <Button
        disabled={disableSubmit}
        loading={isPending}
        onClick={onSubmit}
        type={onSubmit ? 'button' : 'submit'}
      >
        {customLabel ?? submitCustomLabel}
      </Button>
    </Stack>
  );
};
